export type SpeedReadingLessonStatus = 'DRAFT' | 'PROCESSING' | 'READY' | 'FAILED';

export interface SpeedReadingCourse {
  id: string;
  title: string;
  description: string | null;
  order: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  coverMedia?: { id: string; url: string; mimeType?: string } | null;
  lessons?: Pick<SpeedReadingLesson, 'id' | 'title' | 'status' | 'audioDurationMs' | 'order' | 'isActive'>[];
}

export interface CreateCoursePayload {
  title: string;
  description?: string;
  cover?: File | null;
  order?: number;
  isActive?: boolean;
}

export interface LessonMediaRef {
  id: string;
  url: string;
  mimeType?: string;
  name?: string;
  size?: number;
}

export interface SpeedReadingVisualMaterial {
  id?: string;
  title: string;
  pageLabel: string;
  note?: string;
  order?: number;
  visualCategory?: 'reading' | 'question' | 'memory_pegs' | 'number_tracking' | 'word_alternatives' | 'horizontal_zigzag' | 'three_column_grid' | 'balloon_reading' | 'star_reading' | 'gap_reading' | 'measurement_text' | string;
  measurementTextRole?: 'reading' | 'question' | string;
  media?: LessonMediaRef | null;
  file?: File | null;
  isQuestionVisual?: boolean;
  trackTime?: boolean;
  questionCount?: number;
  optionCount?: number;
  correctAnswers?: Array<number | null>;
  memoryPegCount?: number;
  memoryPegLabels?: string[];
  numberTrackingCount?: number;
  numberTrackingDurationSec?: number;
  numberTrackingLineXPercent?: number;
  numberTrackingItems?: Array<{
    value: number;
    centerXPercent: number;
    centerYPercent: number;
    widthPercent?: number;
    heightPercent?: number;
  }>;
  extractedQuestions?: Array<{
    index: number;
    question: string;
    options: string[];
    correctOptionIndex?: number | null;
  }>;
  wordAlternativeExercises?: Array<{
    index: number;
    label: string;
    sentenceParts: string[];
    options: Array<{
      number: number;
      word: string;
    }>;
  }>;
  balloonReadingItems?: Array<{
    index: number;
    rowIndex: number;
    text?: string;
    centerXPercent: number;
    centerYPercent: number;
    widthPercent: number;
    heightPercent: number;
  }>;
  balloonReadingDurationSec?: number;
  starReadingDurationSec?: number;
  gapReadingDurationSec?: number;
  eyeExerciseMode?: string;
  eyeExerciseDurationSec?: number;
  metronomeBpm?: number;
}

export interface LessonVisualCheckpoint {
  id: string;
  title?: string;
  checkpointRole?: string;
  anchorText?: string;
  instruction: string;
  requireLastPage?: boolean;
  visualMaterials: SpeedReadingVisualMaterial[];
}

export interface SpeedReadingLesson {
  id: string;
  courseId?: string | null;
  title: string;
  description: string | null;
  voice: string;
  status: SpeedReadingLessonStatus;
  processingError: string | null;
  audioDurationMs: number | null;
  order: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  rawTranscript?: string;
  rawTranscriptPreview?: string;
  cleanedTranscript?: string | null;
  metadata?: {
    lessonSegments?: Array<{
      index: number;
      kind?: 'narration' | 'quiz' | 'visual' | string;
      parentSegmentIndex?: number;
      checkpointPosition?: 'middle' | 'end' | string;
      title?: string;
      narrationText?: string;
      narrationLength?: number;
      speechText?: string;
      durationMs: number;
      audioMedia?: LessonMediaRef | null;
      quiz?: {
        id: string;
        segmentIndex: number;
        parentSegmentIndex?: number;
        checkpointPosition?: 'middle' | 'end' | string;
        sectionTitle?: string;
        question: string;
        options: string[];
        correctOptionIndex: number;
        wrongFeedback?: string;
        feedbackAudioMedia?: (LessonMediaRef & { durationMs?: number }) | null;
      };
      visualCheckpoint?: LessonVisualCheckpoint | null;
    }>;
    visualMaterials?: SpeedReadingVisualMaterial[];
    visualCheckpoints?: LessonVisualCheckpoint[];
    askSegmentQuizzes?: boolean;
    interactionQuizzes?: Array<{
      id?: string;
      ratio?: number;
      timeSec?: number;
      sectionTitle?: string;
      anchorText?: string;
      question: string;
      options: string[];
      correctOptionIndex: number;
      wrongFeedback?: string;
    }>;
    interactionPrompts?: Array<{
      id?: string;
      ratio?: number;
      timeSec?: number;
      question: string;
    }>;
    rawTranscriptLength?: number;
    cleanedTranscriptLength?: number;
    narrationTranscriptLength?: number;
    [key: string]: unknown;
  } | null;
  coverMedia?: LessonMediaRef | null;
  audioMedia?: LessonMediaRef | null;
}

export interface CreateLessonPayload {
  title: string;
  description?: string;
  rawTranscript: string;
  voice?: string;
  cover?: File | null;
  visualMaterials?: SpeedReadingVisualMaterial[];
  askSegmentQuizzes?: boolean;
  order?: number;
  isActive?: boolean;
  courseId?: string | null;
}

export interface AskQuestionResponse {
  question: string;
  answer: string;
  audioBase64: string;
  mimeType: string;
}

export interface InteractionAnswerResponse {
  correct: boolean;
  correctOptionIndex: number;
  feedback: string | null;
  audioBase64: string | null;
  feedbackAudioUrl: string | null;
  mimeType: string | null;
}

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const authHeaders = (): Record<string, string> => {
  const token = localStorage.getItem('authToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const buildLessonFormData = (payload: Partial<CreateLessonPayload>): FormData => {
  const fd = new FormData();
  if (payload.title !== undefined) fd.append('title', payload.title);
  if (payload.description !== undefined) fd.append('description', payload.description ?? '');
  if (payload.rawTranscript !== undefined) fd.append('rawTranscript', payload.rawTranscript);
  if (payload.voice !== undefined) fd.append('voice', payload.voice ?? 'Kore');
  if (payload.order !== undefined) fd.append('order', String(payload.order));
  if (payload.isActive !== undefined) fd.append('isActive', String(payload.isActive));
  if (payload.askSegmentQuizzes !== undefined) fd.append('askSegmentQuizzes', String(payload.askSegmentQuizzes));
  if (payload.courseId !== undefined) fd.append('courseId', payload.courseId ?? '');
  if (payload.cover instanceof File) fd.append('cover', payload.cover);
  if (payload.visualMaterials !== undefined) {
    let fileIndex = 0;
    const visualPayload = payload.visualMaterials.map((material, index) => {
      const item: Record<string, unknown> = {
        id: material.id,
        title: material.title,
        pageLabel: material.pageLabel,
        note: material.note || '',
        order: material.order ?? index,
        mediaId: material.media?.id,
        visualCategory: material.visualCategory || (material.isQuestionVisual ? 'question' : 'reading'),
        measurementTextRole: material.measurementTextRole || '',
        isQuestionVisual: Boolean(material.isQuestionVisual),
        trackTime: material.trackTime !== false,
        questionCount: material.questionCount || 0,
        optionCount: material.optionCount || 0,
        correctAnswers: material.correctAnswers || [],
        memoryPegCount: material.memoryPegCount || 0,
        memoryPegLabels: material.memoryPegLabels || [],
        numberTrackingCount: material.numberTrackingCount || 0,
        numberTrackingDurationSec: material.numberTrackingDurationSec || 60,
        numberTrackingLineXPercent: material.numberTrackingLineXPercent ?? 50,
        numberTrackingItems: material.numberTrackingItems || [],
        balloonReadingItems: material.balloonReadingItems || [],
        balloonReadingDurationSec: material.balloonReadingDurationSec || 180,
        eyeExerciseMode: material.eyeExerciseMode || '',
        eyeExerciseDurationSec: material.eyeExerciseDurationSec || 0,
        metronomeBpm: material.metronomeBpm || 0,
      };
      if (material.file instanceof File) {
        item.fileIndex = fileIndex;
        fd.append('visuals', material.file);
        fileIndex += 1;
      }
      return item;
    });
    fd.append('visualMaterials', JSON.stringify(visualPayload));
  }
  return fd;
};

const handleResponse = async <T>(response: Response): Promise<T> => {
  if (!response.ok) {
    const text = await response.text();
    let message = `HTTP ${response.status}`;
    try {
      const data = JSON.parse(text);
      message = data?.message || data?.error || message;
    } catch {
      if (text) message = text;
    }
    throw new Error(message);
  }
  if (response.status === 204) return null as T;
  return response.json() as Promise<T>;
};

export const TrainingApi = {
  list: async (options: { includeAll?: boolean; courseId?: string } = {}): Promise<SpeedReadingLesson[]> => {
    const params = new URLSearchParams();
    if (options.includeAll) params.set('includeAll', 'true');
    if (options.courseId) params.set('courseId', options.courseId);
    const query = params.toString();
    const url = `${API_BASE_URL}/speed-reading/lessons${query ? `?${query}` : ''}`;
    const response = await fetch(url, {
      headers: { ...authHeaders() },
      cache: 'no-store',
    });
    return handleResponse<SpeedReadingLesson[]>(response);
  },

  get: async (lessonId: string): Promise<SpeedReadingLesson> => {
    const response = await fetch(`${API_BASE_URL}/speed-reading/lessons/${lessonId}`, {
      headers: { ...authHeaders() },
      cache: 'no-store',
    });
    return handleResponse<SpeedReadingLesson>(response);
  },

  create: async (payload: CreateLessonPayload): Promise<SpeedReadingLesson> => {
    const response = await fetch(`${API_BASE_URL}/speed-reading/lessons`, {
      method: 'POST',
      headers: { ...authHeaders() },
      body: buildLessonFormData(payload),
    });
    return handleResponse<SpeedReadingLesson>(response);
  },

  update: async (lessonId: string, payload: Partial<CreateLessonPayload>): Promise<SpeedReadingLesson> => {
    const response = await fetch(`${API_BASE_URL}/speed-reading/lessons/${lessonId}`, {
      method: 'PUT',
      headers: { ...authHeaders() },
      body: buildLessonFormData(payload),
    });
    return handleResponse<SpeedReadingLesson>(response);
  },

  remove: async (lessonId: string): Promise<{ success: boolean }> => {
    const response = await fetch(`${API_BASE_URL}/speed-reading/lessons/${lessonId}`, {
      method: 'DELETE',
      headers: { ...authHeaders() },
    });
    return handleResponse<{ success: boolean }>(response);
  },

  reprocess: async (lessonId: string): Promise<SpeedReadingLesson> => {
    const response = await fetch(`${API_BASE_URL}/speed-reading/lessons/${lessonId}/reprocess`, {
      method: 'POST',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
    });
    return handleResponse<SpeedReadingLesson>(response);
  },

  ask: async (
    lessonId: string,
    options: { audio?: Blob; question?: string; currentTimeSec: number; mimeType?: string }
  ): Promise<AskQuestionResponse> => {
    const fd = new FormData();
    fd.append('currentTimeSec', String(options.currentTimeSec));
    if (options.question) fd.append('question', options.question);
    if (options.audio) {
      const filename = options.mimeType?.includes('mp4') ? 'question.m4a' : 'question.webm';
      fd.append('audio', options.audio, filename);
    }
    const response = await fetch(`${API_BASE_URL}/speed-reading/lessons/${lessonId}/ask`, {
      method: 'POST',
      headers: { ...authHeaders() },
      body: fd,
    });
    return handleResponse<AskQuestionResponse>(response);
  },

  answerInteraction: async (
    lessonId: string,
    interactionId: string,
    selectedOptionIndex: number
  ): Promise<InteractionAnswerResponse> => {
    const response = await fetch(`${API_BASE_URL}/speed-reading/lessons/${lessonId}/interactions/${interactionId}/answer`, {
      method: 'POST',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ selectedOptionIndex }),
    });
    return handleResponse<InteractionAnswerResponse>(response);
  },

  buildEventsUrl: (lessonId: string): string => {
    const token = localStorage.getItem('authToken') || '';
    const url = new URL(`${API_BASE_URL}/speed-reading/lessons/${lessonId}/events`);
    if (token) url.searchParams.set('token', token);
    return url.toString();
  },
};

const buildCourseFormData = (payload: Partial<CreateCoursePayload>): FormData => {
  const fd = new FormData();
  if (payload.title !== undefined) fd.append('title', payload.title);
  if (payload.description !== undefined) fd.append('description', payload.description ?? '');
  if (payload.order !== undefined) fd.append('order', String(payload.order));
  if (payload.isActive !== undefined) fd.append('isActive', String(payload.isActive));
  if (payload.cover instanceof File) fd.append('cover', payload.cover);
  return fd;
};

export const CourseApi = {
  list: async (options: { includeAll?: boolean } = {}): Promise<SpeedReadingCourse[]> => {
    const url = `${API_BASE_URL}/speed-reading/courses${options.includeAll ? '?includeAll=true' : ''}`;
    const response = await fetch(url, { headers: { ...authHeaders() }, cache: 'no-store' });
    return handleResponse<SpeedReadingCourse[]>(response);
  },

  get: async (courseId: string, options: { includeAll?: boolean } = {}): Promise<SpeedReadingCourse> => {
    const url = `${API_BASE_URL}/speed-reading/courses/${courseId}${options.includeAll ? '?includeAll=true' : ''}`;
    const response = await fetch(url, { headers: { ...authHeaders() }, cache: 'no-store' });
    return handleResponse<SpeedReadingCourse>(response);
  },

  create: async (payload: CreateCoursePayload): Promise<SpeedReadingCourse> => {
    const response = await fetch(`${API_BASE_URL}/speed-reading/courses`, {
      method: 'POST',
      headers: { ...authHeaders() },
      body: buildCourseFormData(payload),
    });
    return handleResponse<SpeedReadingCourse>(response);
  },

  update: async (courseId: string, payload: Partial<CreateCoursePayload>): Promise<SpeedReadingCourse> => {
    const response = await fetch(`${API_BASE_URL}/speed-reading/courses/${courseId}`, {
      method: 'PUT',
      headers: { ...authHeaders() },
      body: buildCourseFormData(payload),
    });
    return handleResponse<SpeedReadingCourse>(response);
  },

  remove: async (courseId: string): Promise<{ success: boolean }> => {
    const response = await fetch(`${API_BASE_URL}/speed-reading/courses/${courseId}`, {
      method: 'DELETE',
      headers: { ...authHeaders() },
    });
    return handleResponse<{ success: boolean }>(response);
  },
};

export const formatLessonDuration = (durationMs: number | null | undefined): string => {
  if (!durationMs || durationMs < 1000) return '—';
  const totalSec = Math.round(durationMs / 1000);
  const mins = Math.floor(totalSec / 60);
  const secs = totalSec % 60;
  if (mins === 0) return `${secs} sn`;
  return `${mins} dk ${secs.toString().padStart(2, '0')} sn`;
};
